// Family Account support tools: reason rules and error text.
//
// The reason rule mirrors affiliate-manager-be and epoint-be: whitespace runs
// collapse to one space, then 3–200 characters and no '@' (the reason is
// stored in the family audit log next to the admin username, never an
// e-mail address).

export const FAMILY_REASON_MIN_LENGTH = 3;
export const FAMILY_REASON_MAX_LENGTH = 200;

export type FamilyReasonError =
  | "REASON_REQUIRED"
  | "REASON_TOO_SHORT"
  | "REASON_TOO_LONG"
  | "REASON_CONTAINS_EMAIL";

export const FAMILY_NETWORK_ERROR_MESSAGE = "Network error. Please try again.";
export const FAMILY_GENERIC_ERROR_MESSAGE =
  "Something went wrong. Please try again.";
export const FAMILY_TIMEOUT_ERROR_MESSAGE =
  "The request timed out. Reload the family view to check whether the action went through before trying again.";

export const FAMILY_ADMIN_ERROR_MESSAGES: Record<string, string> = {
  REASON_REQUIRED: "Enter a reason for this action.",
  REASON_TOO_SHORT: `The reason must be at least ${FAMILY_REASON_MIN_LENGTH} characters.`,
  REASON_TOO_LONG: `The reason must be at most ${FAMILY_REASON_MAX_LENGTH} characters.`,
  REASON_CONTAINS_EMAIL: "The reason must not contain an e-mail address.",
  INVALID_ID:
    "This family record has an invalid id. Reload the page and try again.",
  MEMBER_NOT_TERMINAL:
    "Only an ended membership or invitation can be erased. Remove the member or cancel the invitation first.",
  INVITE_NOT_PENDING:
    "This invitation is no longer pending. Reload the family view.",
  MEMBER_NOT_FOUND:
    "This family member was not found or is no longer active. Reload the family view.",
  INVITATION_NOT_FOUND:
    "This invitation was not found. Reload the family view.",
  GOAL_NOT_FOUND: "This family has no goal any more. Reload the family view.",
  FAMILY_NOT_FOUND: "Family account not found.",
  FAMILY_CLOSED: "This family account was closed. Reload the family view.",
};

const STATUS_MESSAGES: Record<number, string> = {
  // A 404 without a family code means the route itself is missing (for
  // example "Cannot GET /..."), which is no text for an admin.
  404: "The family service could not handle this request. Please try again, and contact the tech team if it keeps failing.",
  503: "The family service is not available right now. Please try again in a moment.",
  504: "The family service did not respond in time. Reload the family view to check whether the action went through before trying again.",
};

/** Trims and collapses whitespace runs (newlines included) to one space. */
export function normalizeReason(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

/** Validates the normalised reason; null when it may be sent. */
export function validateReason(
  value: string | null | undefined,
): FamilyReasonError | null {
  const reason = normalizeReason(value);
  if (!reason) return "REASON_REQUIRED";
  if (reason.length < FAMILY_REASON_MIN_LENGTH) return "REASON_TOO_SHORT";
  if (reason.length > FAMILY_REASON_MAX_LENGTH) return "REASON_TOO_LONG";
  if (reason.includes("@")) return "REASON_CONTAINS_EMAIL";
  return null;
}

/** Text for a reason validation result (the modal's inline error). */
export function reasonErrorMessage(error: FamilyReasonError): string {
  return FAMILY_ADMIN_ERROR_MESSAGES[error];
}

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  response?: {
    status?: unknown;
    data?: unknown;
  };
}

function asObject(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

/** The backend's error `code` (e.g. MEMBER_NOT_TERMINAL), if any. */
export function familyAdminErrorCode(error: unknown): string | undefined {
  const data = asObject((error as ErrorLike | null)?.response?.data);
  return typeof data?.code === "string" ? data.code : undefined;
}

/**
 * Admin-facing text for a failed family support call. Never blank: known
 * codes and 404 / 503 / 504 without a code get fixed copy, other answers show the backend
 * message (a validation array is joined), and a request without an answer
 * shows the network fallback.
 */
export function familyAdminErrorMessage(error: unknown): string {
  const err = asObject(error) as ErrorLike | null;
  const response = err?.response;

  if (!response) {
    if (err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT") {
      return FAMILY_TIMEOUT_ERROR_MESSAGE;
    }
    return FAMILY_NETWORK_ERROR_MESSAGE;
  }

  const code = familyAdminErrorCode(error);
  if (
    code &&
    Object.prototype.hasOwnProperty.call(FAMILY_ADMIN_ERROR_MESSAGES, code)
  ) {
    return FAMILY_ADMIN_ERROR_MESSAGES[code];
  }

  const status = typeof response.status === "number" ? response.status : 0;
  if (!code && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  const data = asObject(response.data);
  const message = data ? data.message : response.data;
  if (Array.isArray(message)) {
    const parts = message
      .filter((part): part is string => typeof part === "string")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  } else if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  return STATUS_MESSAGES[status] ?? FAMILY_GENERIC_ERROR_MESSAGE;
}
